import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "dash-wrapper" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = { class: "header-content" }
const _hoisted_4 = { class: "header-right" }
const _hoisted_5 = { class: "dash-body" }
const _hoisted_6 = { class: "kpi-row" }
const _hoisted_7 = { class: "kpi-card" }
const _hoisted_8 = { class: "kpi-body" }
const _hoisted_9 = { class: "kpi-val" }
const _hoisted_10 = { class: "kpi-card" }
const _hoisted_11 = { class: "kpi-body" }
const _hoisted_12 = { class: "kpi-val" }
const _hoisted_13 = { class: "kpi-card" }
const _hoisted_14 = { class: "kpi-body" }
const _hoisted_15 = { class: "kpi-val" }
const _hoisted_16 = { class: "kpi-body" }
const _hoisted_17 = { class: "kpi-body" }
const _hoisted_18 = { class: "kpi-lbl" }
const _hoisted_19 = { class: "content-row" }
const _hoisted_20 = { class: "chart-card" }
const _hoisted_21 = { class: "section-header" }
const _hoisted_22 = { class: "chart-body" }
const _hoisted_23 = {
  key: 1,
  class: "empty-chart"
}
const _hoisted_24 = { style: {"color":"#94a3b8","font-size":"13px","margin-top":"8px"} }
const _hoisted_25 = { class: "top-card" }
const _hoisted_26 = { class: "val-bold" }
const _hoisted_27 = {
  key: 0,
  class: "summary-bar"
}
const _hoisted_28 = { class: "sum-item" }
const _hoisted_29 = { class: "sum-lbl" }
const _hoisted_30 = { class: "sum-val green" }
const _hoisted_31 = { class: "sum-item" }
const _hoisted_32 = { class: "sum-lbl" }
const _hoisted_33 = { class: "sum-val red" }
const _hoisted_34 = { class: "sum-item" }
const _hoisted_35 = { class: "sum-item" }
const _hoisted_36 = { class: "sum-val" }

import { ref, computed, onMounted } from "vue";
import {
  DxChart, DxSeries, DxCommonSeriesSettings,
  DxArgumentAxis, DxValueAxis, DxLabel,
  DxLegend, DxTooltip,
} from "devextreme-vue/chart";
import {
  DxDataGrid, DxColumn, DxPaging, DxSorting,
} from "devextreme-vue/data-grid";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxButton } from "devextreme-vue/button";
import { DxLoadPanel } from "devextreme-vue/load-panel";
import http from "@/settings/apiSettings";
import GES_StocService from "@/services/GES/GES_Stoc";

interface MonthStat {
  luna: string;
  lunaIdx: number;
  ValoareIntrata: number;
  ValoareIesita: number;
  NrIntrari: number;
  NrIesiri: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardStocuri',
  setup(__props) {

const MONTHS_RO = ['Ian','Feb','Mar','Apr','Mai','Iun','Iul','Aug','Sep','Oct','Nov','Dec'];

const gestiuni = ref<any[]>([]);
const isLoading = ref(false);
const filterGestiuneId = ref<string | null>(null);

const anCurent = new Date().getFullYear();
const yearStart = `${anCurent}-01-01`;
const today = new Date().toISOString().split("T")[0];

// Stoc snapshot
const stocData = ref<any[]>([]);
// Mișcări an curent
const miscariData = ref<any[]>([]);

onMounted(async () => {
  try {
    const resp = await http.clientStocuriOdata.get("GES_Gestiuni");
    gestiuni.value = resp.data?.value ?? resp.data ?? [];
  } catch { /* silent */ }
  await loadDate();
});

async function loadDate() {
  isLoading.value = true;
  const gestiuneId = filterGestiuneId.value ?? undefined;
  try {
    const [stocResp, miscariResp] = await Promise.all([
      GES_StocService.getStocCurent(today, gestiuneId, 0, true),
      GES_StocService.getMiscari(yearStart, today, gestiuneId),
    ]);
    stocData.value = stocResp.data ?? [];
    miscariData.value = miscariResp.data ?? [];
  } catch { /* silent */ } finally {
    isLoading.value = false;
  }
}

// KPI
const totalProduse = computed(() => stocData.value.filter(s => (s.Stoc ?? 0) > 0).length);
const valoareTotala = computed(() => stocData.value.reduce((acc, s) => acc + (s.Valoare ?? 0), 0));
const gestiuniActive = computed(() => new Set(stocData.value.filter(s => (s.Stoc ?? 0) > 0).map(s => s.GestiuneId)).size);
const stocNegativCount = computed(() => stocData.value.filter(s => (s.Stoc ?? 0) < 0).length);

const faraMiscareCount = computed(() => {
  const withMovement = new Set(miscariData.value.map(m => `${m.ProdusId}|${m.GestiuneId}`));
  return stocData.value.filter(s => (s.Stoc ?? 0) > 0 && !withMovement.has(`${s.ProdusId}|${s.GestiuneId}`)).length;
});

// Top 20 produse după valoare
const topProduse = computed(() =>
  [...stocData.value]
    .filter(s => (s.Stoc ?? 0) > 0)
    .sort((a, b) => (b.Valoare ?? 0) - (a.Valoare ?? 0))
    .slice(0, 20)
);

// Chart data — mișcări groupate pe lună
const chartData = computed<MonthStat[]>(() => {
  const map = new Map<number, MonthStat>();
  for (let i = 0; i < 12; i++) {
    const d = new Date(anCurent, i, 1);
    if (d > new Date()) break;
    map.set(i, { luna: MONTHS_RO[i], lunaIdx: i, ValoareIntrata: 0, ValoareIesita: 0, NrIntrari: 0, NrIesiri: 0 });
  }
  for (const m of miscariData.value) {
    const luna = new Date(m.Data).getMonth();
    const entry = map.get(luna);
    if (!entry) continue;
    if (m.TipMiscare === "Intrare") {
      entry.ValoareIntrata += m.Valoare ?? 0;
      entry.NrIntrari++;
    } else {
      entry.ValoareIesita += m.Valoare ?? 0;
      entry.NrIesiri++;
    }
  }
  return Array.from(map.values());
});

const totalIntrat = computed(() => miscariData.value.filter(m => m.TipMiscare === "Intrare").reduce((a, m) => a + (m.Valoare ?? 0), 0));
const totalIesit = computed(() => miscariData.value.filter(m => m.TipMiscare === "Ieșire").reduce((a, m) => a + (m.Valoare ?? 0), 0));
const totalMiscari = computed(() => miscariData.value.length);

// Helpers
function fmtMoney(val: number): string {
  if (val >= 1_000_000) return (val / 1_000_000).toFixed(2) + " M";
  if (val >= 1_000) return (val / 1_000).toFixed(1) + " K";
  return new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
}

function fmtNum(val: number, dec: number): string {
  return new Intl.NumberFormat("ro-RO", { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(val ?? 0);
}

function formatAxisVal(e: any): string {
  const v = e.value as number;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(0) + "K";
  return v.toFixed(0);
}

function customizeTooltip(point: any): object {
  return {
    text: `${point.seriesName}\n${fmtNum(point.value, 2)} lei`,
  };
}

function rankClass(idx: number): string {
  if (idx === 0) return "rank-gold";
  if (idx === 1) return "rank-silver";
  if (idx === 2) return "rank-bronze";
  return "rank-default";
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[1] || (_cache[1] = _createElementVNode("i", { class: "dx-icon dx-icon-chart header-icon" }, null, -1)),
        _cache[2] || (_cache[2] = _createElementVNode("div", null, [
          _createElementVNode("div", { class: "header-title" }, "Dashboard Stocuri"),
          _createElementVNode("div", { class: "header-subtitle" }, "Situație generală · Evoluție mișcări · Top produse")
        ], -1)),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(DxSelectBox), {
            value: filterGestiuneId.value,
            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((filterGestiuneId).value = $event)),
            "data-source": gestiuni.value,
            "display-expr": "Nume",
            "value-expr": "Id",
            placeholder: "Toate gestiunile",
            "show-clear-button": true,
            width: 220,
            "styling-mode": "outlined",
            class: "gestiune-select"
          }, null, 8, ["value", "data-source"]),
          _createVNode(_unref(DxButton), {
            icon: "refresh",
            hint: 'Actualizează',
            onClick: loadDate,
            disabled: isLoading.value,
            "styling-mode": "text",
            class: "refresh-btn"
          }, null, 8, ["disabled"])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(_unref(DxLoadPanel), {
        visible: isLoading.value,
        "show-indicator": true,
        "show-pane": true,
        message: "Se încarcă datele...",
        position: { of: '.dash-body' }
      }, null, 8, ["visible"]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          _cache[4] || (_cache[4] = _createElementVNode("div", {
            class: "kpi-icon",
            style: {"background":"linear-gradient(135deg,#2563eb,#60a5fa)"}
          }, [
            _createElementVNode("i", { class: "dx-icon dx-icon-product" })
          ], -1)),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, _toDisplayString(totalProduse.value.toLocaleString('ro-RO')), 1),
            _cache[3] || (_cache[3] = _createElementVNode("div", { class: "kpi-lbl" }, "Produse în stoc", -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _cache[6] || (_cache[6] = _createElementVNode("div", {
            class: "kpi-icon",
            style: {"background":"linear-gradient(135deg,#059669,#34d399)"}
          }, [
            _createElementVNode("i", { class: "dx-icon dx-icon-money" })
          ], -1)),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, _toDisplayString(fmtMoney(valoareTotala.value)), 1),
            _cache[5] || (_cache[5] = _createElementVNode("div", { class: "kpi-lbl" }, "Valoare totală stoc", -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_13, [
          _cache[8] || (_cache[8] = _createElementVNode("div", {
            class: "kpi-icon",
            style: {"background":"linear-gradient(135deg,#7c3aed,#a78bfa)"}
          }, [
            _createElementVNode("i", { class: "dx-icon dx-icon-group" })
          ], -1)),
          _createElementVNode("div", _hoisted_14, [
            _createElementVNode("div", _hoisted_15, _toDisplayString(gestiuniActive.value), 1),
            _cache[7] || (_cache[7] = _createElementVNode("div", { class: "kpi-lbl" }, "Gestiuni active", -1))
          ])
        ]),
        _createElementVNode("div", {
          class: _normalizeClass(["kpi-card", stocNegativCount.value > 0 ? 'kpi-alert' : ''])
        }, [
          _createElementVNode("div", {
            class: "kpi-icon",
            style: _normalizeStyle(stocNegativCount.value > 0
            ? 'background: linear-gradient(135deg,#dc2626,#f87171)'
            : 'background: linear-gradient(135deg,#64748b,#94a3b8)')
          }, [...(_cache[9] || (_cache[9] = [
            _createElementVNode("i", { class: "dx-icon dx-icon-warning" }, null, -1)
          ]))], 4),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("div", {
              class: _normalizeClass(["kpi-val", stocNegativCount.value > 0 ? 'val-red' : ''])
            }, _toDisplayString(stocNegativCount.value), 3),
            _cache[10] || (_cache[10] = _createElementVNode("div", { class: "kpi-lbl" }, "Produse stoc negativ", -1))
          ])
        ], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["kpi-card", faraMiscareCount.value > 0 ? 'kpi-warn' : ''])
        }, [
          _createElementVNode("div", {
            class: "kpi-icon",
            style: _normalizeStyle(faraMiscareCount.value > 0
            ? 'background: linear-gradient(135deg,#d97706,#fbbf24)'
            : 'background: linear-gradient(135deg,#64748b,#94a3b8)')
          }, [...(_cache[11] || (_cache[11] = [
            _createElementVNode("i", { class: "dx-icon dx-icon-clock" }, null, -1)
          ]))], 4),
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("div", {
              class: _normalizeClass(["kpi-val", faraMiscareCount.value > 0 ? 'val-orange' : ''])
            }, _toDisplayString(faraMiscareCount.value), 3),
            _createElementVNode("div", _hoisted_18, "Fără mișcare (" + _toDisplayString(_unref(anCurent)) + ")", 1)
          ])
        ], 2)
      ]),
      _createElementVNode("div", _hoisted_19, [
        _createElementVNode("div", _hoisted_20, [
          _createElementVNode("div", _hoisted_21, [
            _cache[12] || (_cache[12] = _createElementVNode("i", { class: "dx-icon dx-icon-chart" }, null, -1)),
            _createElementVNode("span", null, "Evoluție mișcări " + _toDisplayString(_unref(anCurent)), 1),
            _cache[13] || (_cache[13] = _createElementVNode("div", { class: "chart-legend" }, [
              _createElementVNode("span", {
                class: "leg-dot",
                style: {"background":"#059669"}
              }),
              _createTextVNode(" Intrări "),
              _createElementVNode("span", {
                class: "leg-dot",
                style: {"background":"#dc2626","margin-left":"12px"}
              }),
              _createTextVNode(" Ieșiri ")
            ], -1))
          ]),
          _createElementVNode("div", _hoisted_22, [
            (chartData.value.length)
              ? (_openBlock(), _createBlock(_unref(DxChart), {
                  key: 0,
                  "data-source": chartData.value,
                  animation: { enabled: true },
                  palette: ['#059669','#dc2626']
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(DxCommonSeriesSettings), {
                      "argument-field": "luna",
                      type: "bar",
                      "bar-padding": 0.2
                    }),
                    _createVNode(_unref(DxSeries), {
                      "value-field": "ValoareIntrata",
                      name: "Intrări",
                      color: "#059669"
                    }),
                    _createVNode(_unref(DxSeries), {
                      "value-field": "ValoareIesita",
                      name: "Ieșiri",
                      color: "#dc2626"
                    }),
                    _createVNode(_unref(DxArgumentAxis), null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(DxLabel), {
                          "word-wrap": 'none',
                          "overlapping-behavior": 'none'
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(DxValueAxis), null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(DxLabel), { "customize-text": formatAxisVal })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(DxLegend), {
                      "vertical-alignment": "bottom",
                      "horizontal-alignment": "center",
                      visible: false
                    }),
                    _createVNode(_unref(DxTooltip), {
                      enabled: true,
                      "customize-tooltip": customizeTooltip
                    })
                  ]),
                  _: 1
                }, 8, ["data-source"]))
              : (!isLoading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                    _cache[14] || (_cache[14] = _createElementVNode("i", {
                      class: "dx-icon dx-icon-chart",
                      style: {"font-size":"40px","color":"#e2e8f0"}
                    }, null, -1)),
                    _createElementVNode("div", _hoisted_24, "Nu există mișcări în " + _toDisplayString(_unref(anCurent)), 1)
                  ]))
                : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("div", _hoisted_25, [
          _cache[15] || (_cache[15] = _createElementVNode("div", { class: "section-header" }, [
            _createElementVNode("i", { class: "dx-icon dx-icon-orderedlist" }),
            _createElementVNode("span", null, "Top 20 produse după valoare stoc")
          ], -1)),
          _createVNode(_unref(DxDataGrid), {
            "data-source": topProduse.value,
            height: "100%",
            "show-borders": false,
            "show-column-lines": false,
            "show-row-lines": true,
            "row-alternation-enabled": true,
            "hover-state-enabled": true,
            "column-auto-width": false
          }, {
            rankTemplate: _withCtx(({ rowIndex }) => [
              _createElementVNode("span", {
                class: _normalizeClass(["rank-badge", rankClass(rowIndex)])
              }, _toDisplayString(rowIndex + 1), 3)
            ]),
            valTemplate: _withCtx(({ data }) => [
              _createElementVNode("span", _hoisted_26, _toDisplayString(fmtNum(data.value, 2)), 1)
            ]),
            default: _withCtx(() => [
              _createVNode(_unref(DxPaging), { "page-size": 20 }),
              _createVNode(_unref(DxSorting), { mode: "single" }),
              _createVNode(_unref(DxColumn), {
                width: 30,
                caption: "#",
                "cell-template": "rankTemplate"
              }),
              _createVNode(_unref(DxColumn), {
                "data-field": "ProdusNume",
                caption: "Produs",
                "min-width": 130,
                "css-class": "col-produs"
              }),
              _createVNode(_unref(DxColumn), {
                "data-field": "Stoc",
                caption: "Stoc",
                width: 80,
                "data-type": "number",
                format: { type: 'fixedPoint', precision: 2 }
              }),
              _createVNode(_unref(DxColumn), {
                "data-field": "Valoare",
                caption: "Valoare",
                width: 110,
                "data-type": "number",
                format: { type: 'fixedPoint', precision: 2 },
                "cell-template": "valTemplate",
                "sort-index": 0,
                "sort-order": "desc"
              })
            ]),
            _: 1
          }, 8, ["data-source"])
        ])
      ]),
      (totalIntrat.value > 0 || totalIesit.value > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
            _createElementVNode("div", _hoisted_28, [
              _createElementVNode("span", _hoisted_29, "Total intrări " + _toDisplayString(_unref(anCurent)), 1),
              _createElementVNode("span", _hoisted_30, _toDisplayString(fmtMoney(totalIntrat.value)), 1)
            ]),
            _cache[18] || (_cache[18] = _createElementVNode("div", { class: "sum-divider" }, null, -1)),
            _createElementVNode("div", _hoisted_31, [
              _createElementVNode("span", _hoisted_32, "Total ieșiri " + _toDisplayString(_unref(anCurent)), 1),
              _createElementVNode("span", _hoisted_33, _toDisplayString(fmtMoney(totalIesit.value)), 1)
            ]),
            _cache[19] || (_cache[19] = _createElementVNode("div", { class: "sum-divider" }, null, -1)),
            _createElementVNode("div", _hoisted_34, [
              _cache[16] || (_cache[16] = _createElementVNode("span", { class: "sum-lbl" }, "Sold net", -1)),
              _createElementVNode("span", {
                class: _normalizeClass(["sum-val", totalIntrat.value - totalIesit.value >= 0 ? 'green' : 'red'])
              }, _toDisplayString(fmtMoney(totalIntrat.value - totalIesit.value)), 3)
            ]),
            _cache[20] || (_cache[20] = _createElementVNode("div", { class: "sum-divider" }, null, -1)),
            _createElementVNode("div", _hoisted_35, [
              _cache[17] || (_cache[17] = _createElementVNode("span", { class: "sum-lbl" }, "Nr. mișcări", -1)),
              _createElementVNode("span", _hoisted_36, _toDisplayString(totalMiscari.value.toLocaleString('ro-RO')), 1)
            ])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})